import { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import useSWR from 'swr'
import { fetcher } from '@/hooks'
import { cn } from '@/lib/utils'
import { formatUSD, formatPercent, formatMultiplier } from '@/lib/format-utils'
import { useRoasStore } from '@/stores/roas'
import { getColorByValue } from '../../../lib/color-rules'
import { LineChartIcon, ArrowUp, ArrowDown, ArrowUpDown, ChevronLast, ChevronFirst, Info } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { CreateNote } from './note-create-dialog'
import CampaignStatusButton from '@/components/roas/camps/campaign-status-button'
import dayjs from 'dayjs'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import Day0Chart from './roas-trend-chart'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { shortCampaignLabel } from '../utils'
import {
  IndexColumn,
  InstallDateColumn,
  InstallsColumn,
  RevenueColumn,
  MoneyCell,
} from './roas-table-columns'
import { ROASDailyDataTable, baseSortedData, TargetDays } from './base-virtualized-table'
import { exportToCsv } from '@/lib/utils'
import { BenchmarkView } from './benchmark/benchmark-view'
import {
  getDisplayableRoasBaseline,
  getRoasBaselineSource,
  resolveRoasBaseline,
  TOTAL_FALLBACK_STANDARD_MODE,
} from '@/lib/dr-utils'
import useRoiTableStore from '@/store/roi-table'
import { obtainAppNameByPid } from '@/comm/app'

const ROAS_TIPS = [
  'DN的数字是由最近的30个有效数据计算得出(原始数据计算平均)',
  'D0的第一个数字是目标ROAS',
  'UnityAds Int 数据使用的是 UTC0 时区',
  '倍率公式(D0 Rev): 分子(Daily/Cum收入) / D0真实收入',
  '倍率公式(Target): 分子(Daily/Cum收入) / (Cost * D0目标ROAS)',
  '倍率公式(Growth): (当前收入 - 前一日收入) / 前一日收入',
]

const getActiveRoasBaseline = (roasLine, totalFallbackMap, targetDay, date) =>
  getDisplayableRoasBaseline(
    roasLine,
    totalFallbackMap,
    targetDay,
    date,
    TOTAL_FALLBACK_STANDARD_MODE,
  )

const hasUsableTargetRoas = (roasLine, totalFallbackMap) => {
  if (!roasLine) return false
  if (typeof roasLine === 'number') return roasLine > 0

  const day0Baselines = roasLine[0]
  if (!day0Baselines || typeof day0Baselines !== 'object') return false

  const dates = Object.keys(day0Baselines)
  if (dates.length === 0) return false

  if (TOTAL_FALLBACK_STANDARD_MODE !== 'hide') return true

  return dates.some((date) => !totalFallbackMap?.[0]?.[date])
}

const getPredictionRevenueAtDay = (row, dayKey, showCumulative) => {
  if (!row) return 0

  if (showCumulative) {
    if (
      row.predictRevenueAccDays &&
      row.predictRevenueAccDays[dayKey] !== undefined &&
      row.predictRevenueAccDays[dayKey] !== null
    ) {
      return Number(row.predictRevenueAccDays[dayKey]) || 0
    }
    if (row.cumulativePredictRevenueDays) {
      return Number(row.cumulativePredictRevenueDays[dayKey]) || 0
    }
  }

  const predictDays = row.predictRevenueDays || {}
  if (!showCumulative) {
    return Number(predictDays[dayKey]) || 0
  }

  const targetDay = Number(dayKey.replace('day', ''))
  let sum = 0
  for (let i = 0; i <= targetDay; i++) {
    sum += Number(predictDays[`day${i}`]) || 0
  }
  return sum
}

const hasPredictionAtDay = (row, dayKey, showCumulative) => {
  if (!row) return false
  if (showCumulative) {
    return (
      row.predictRevenueAccDays &&
      row.predictRevenueAccDays[dayKey] !== undefined &&
      row.predictRevenueAccDays[dayKey] !== null
    )
  }
  return row.predictRevenueDays?.[dayKey] !== undefined
}

/**
 * 读取官方 ROAS 预测（roasAcc）。仅用于 ROAS/倍率列，不与收入字段混用。
 * 返回 roasAcc 原始值（小数形式，e.g. 0.87），可直接传给 formatPercent。
 */
const getPredictionRoasAtDay = (row, dayKey) =>
  row?.predictRoasAccDays?.[dayKey] ?? undefined

/**
 * 判断当前 dayKey 是否有官方 ROAS 预测数据可展示。
 */
const hasPredictionRoasAtDay = (row, dayKey) =>
  row?.predictRoasAccDays?.[dayKey] !== undefined &&
  row?.predictRoasAccDays?.[dayKey] !== null

const TipsTicker = () => {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % ROAS_TIPS.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="flex items-center gap-1 overflow-hidden flex-1 min-w-0 max-w-[500px]">
      <Dialog>
        <DialogTrigger asChild>
          <div className="flex items-center gap-1 cursor-pointer flex-1 min-w-0 group">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground group-hover:text-destructive flex-shrink-0"
            >
              <Info className="h-4 w-4" />
            </Button>
            <div className="flex-1 overflow-hidden relative h-6 flex items-center">
              {ROAS_TIPS.map((tip, i) => (
                <div
                  key={i}
                  className={cn(
                    'absolute inset-0 flex items-center transition-all duration-500 text-xs text-destructive truncate',
                    i === index
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-4 opacity-0 pointer-events-none',
                  )}
                >
                  {tip}
                </div>
              ))}
            </div>
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>重要信息说明</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {ROAS_TIPS.map((tip, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <span className="text-destructive font-bold">•</span>
                <span className="text-muted-foreground">{tip}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Total target days configuration
const getNoteDate = () => {
  return dayjs().format('YYYY-MM-DD')
}

export const LtvCell = ({ row, historyDay }) => {
  const totalPredictRevenue = useMemo(() => {
    const historyDaysRevenue = Array.from(
      { length: historyDay },
      (_, i) => row.days[`day${i}`] ?? 0,
    ).reduce((acc, value) => acc + value, 0)
    // Prediction data is now fetched separately - using actual revenue only
    return historyDaysRevenue
  }, [row.days, historyDay])

  const totalRevenue = useMemo(() => {
    return Object.values(row.days).reduce((acc, value) => acc + value, 0)
  }, [row.days])

  const ltv = totalRevenue / row.installs
  const predictLtv = totalPredictRevenue / row.installs
  return (
    <div className="flex flex-col">
      <div className={`text-right text-base text-black`}>
        {formatUSD(ltv, 4, 4)}
      </div>
      <div className="text-right text-base text-xs text-gray-500 font-normal">
        {formatUSD(predictLtv, 4, 4)}
      </div>
    </div>
  )
}

export const CostCell = ({ cost, totalCost }) => {
  return (
    <div className="flex flex-col">
      <div className="text-right text-base text-black">{formatUSD(cost)}</div>
      <div className="text-right text-base text-xs text-gray-500">
        {formatUSD(totalCost)}/{formatPercent(cost / totalCost, 1, 1)}
      </div>
    </div>
  )
}

export const ProfitCell = ({ revenue, cost }) => {
  if (!revenue || !cost) {
    return (
      <div className="text-right text-base text-black">
        <span className="text-muted-foreground">-</span>
      </div>
    )
  }
  const profit = revenue - cost
  const colorClass = profit >= 0 ? 'text-green-600' : 'text-red-600'
  return (
    <div className={`text-right text-base text-black ${colorClass}`}>
      {formatUSD(profit)}
    </div>
  )
}

export const getRoasColor = (value, roasline, roiline) => {
  if (!value) return ''
  if (value / 100 >= roasline) return 'text-green-600'
  if (value / 100 <= roiline) return 'text-red-600'
  return ''
}

export const RoasCell = ({ value, roasline, roiline }) => {
  if (value === undefined || value === null) {
    return (
      <div className="text-right text-base text-black">
        <span className="text-muted-foreground">-</span>
      </div>
    )
  }
  const colorClass = getRoasColor(value, roasline, roiline)
  return (
    <div className="flex flex-col">
      <div
        className={`text-right font-bold text-base text-black ${colorClass}`}
      >
        {formatPercent(value / 100, 2, 2)}
      </div>
      {roasline && (
        <div className="text-right text-base  text-xs text-gray-500 font-normal">
          {formatPercent(roasline, 2, 2)}
        </div>
      )}
      {roiline && (
        <div className="text-right text-base text-xs text-red-600 font-normal">
          {formatPercent(roiline, 2, 2)}
        </div>
      )}
    </div>
  )
}

const DayCell = ({
  dayIndex,
  row,
  day0RoasLine,
  day0RoasLineTotalFallback,
  isPrediction,
}) => {
  const { displayMode, dayMode, multiplierDenominatorMode } = useRoasStore()
  const showPercentage = displayMode === 'percentage'
  const showMultiplier = displayMode === 'multiplier'
  const showCumulative = isPrediction ? true : dayMode === 'cumulative'

  const days = row.days || {}
  const predictDays = row.predictRevenueDays || {}
  
  // Use applicable cost for the specific day if in summary mode to avoid distortion
  const dayKey = `day${dayIndex}`
  const cost = row.dayCosts?.[dayKey] || row.cost
  const predictCost = row.predictDayCosts?.[dayKey] || row.cost

  const iaaDays = row.iaa || {} // 获取 IAA 收入数据
  const iapDays = row.iap || {} // 获取 IAP 收入数据
  const installDate = row.install_date // 获取安装日期，用于获取对应的 baseline 

  const [
    value,
    predictValue,
    currentIaaValue,
    currentPredictIaaValue,
    currentIapValue,
    currentPredictIapValue,
  ] = useMemo(() => {
    let totalRevenue = 0 // 总收入
    let predictTotalRevenue = 0 // 预测总收入
    let iaaRevenue = 0 // IAA 收入
    let predictIaaRevenue = 0 // 预测 IAA 收入
    let iapRevenue = 0 // IAP 收入
    let predictIapRevenue = 0 // 预测 IAP 收入

    const dayKey = `day${dayIndex}`

    if (showCumulative) {
      // Prediction 列：API 提供 revenue_acc（截至该 DSI 的累计收入）时，禁止对 revenue_daily 再逐日累加，否则会与真实 ROAS 差数量级
      const accPredict =
        row.predictRevenueAccDays &&
        row.predictRevenueAccDays[dayKey] !== undefined &&
        row.predictRevenueAccDays[dayKey] !== null
      if (accPredict) {
        predictTotalRevenue = Number(row.predictRevenueAccDays[dayKey]) || 0
      }

      // In cumulative mode, we use pre-calculated cumulative values if available (from summary mode)
      // Or fallback to summing up (for daily mode or single day view)
      
      // Check if we are in summary mode and have pre-calculated cumulative data
      if (row.cumulativeRevenueDays) {
         totalRevenue = row.cumulativeRevenueDays[dayKey] || 0
         if (!accPredict) {
           predictTotalRevenue = row.cumulativePredictRevenueDays?.[dayKey] || 0
         }
         iaaRevenue = row.cumulativeIaaDays?.[dayKey] || 0
         predictIaaRevenue = row.cumulativeIaaDays?.[dayKey] || 0 // IAA prediction fallback
         iapRevenue = row.cumulativeIapDays?.[dayKey] || 0
         predictIapRevenue = row.cumulativeIapDays?.[dayKey] || 0 // IAP prediction fallback
      } else {
          // Standard daily cumulative calculation
          for (let i = 0; i <= dayIndex; i++) {
            if (days[`day${i}`] !== undefined) {
              const dayTotalRevenue = Number(days[`day${i}`]) || 0
              totalRevenue += dayTotalRevenue
            }
            if (!accPredict && predictDays[`day${i}`] !== undefined) {
              const predictDayTotalRevenue = Number(predictDays[`day${i}`]) || 0
              predictTotalRevenue += predictDayTotalRevenue
            }

            if (iaaDays[`day${i}`] !== undefined) {
              const dayIaaRevenue = Number(iaaDays[`day${i}`]) || 0
              iaaRevenue += dayIaaRevenue
            }
            if (iaaDays[`day${i}`] !== undefined) {
              const predictDayIaaRevenue = Number(iaaDays[`day${i}`]) || 0
              predictIaaRevenue += predictDayIaaRevenue
            }

            if (iapDays[`day${i}`] !== undefined) {
              const dayIapRevenue = Number(iapDays[`day${i}`]) || 0
              iapRevenue += dayIapRevenue
            }
            if (iapDays[`day${i}`] !== undefined) {
              const predictDayIapRevenue = Number(iapDays[`day${i}`]) || 0
              predictIapRevenue += predictDayIapRevenue
            }
          }
      }
    } else {
      totalRevenue = Number(days[`day${dayIndex}`]) || 0
      // Only use prediction data, don't fallback to actual data
      // Backend now returns number type, no need to convert
      predictTotalRevenue = predictDays[`day${dayIndex}`] || 0
      iaaRevenue = Number(iaaDays[`day${dayIndex}`]) || 0
      predictIaaRevenue = Number(iaaDays[`day${dayIndex}`]) || iaaRevenue || 0
      iapRevenue = Number(iapDays[`day${dayIndex}`]) || 0
      predictIapRevenue = Number(iapDays[`day${dayIndex}`]) || iapRevenue || 0
    }
    return [
      totalRevenue,
      predictTotalRevenue,
      iaaRevenue,
      predictIaaRevenue,
      iapRevenue,
      predictIapRevenue,
    ]
  }, [
    days,
    predictDays,
    iaaDays,
    iapDays,
    dayIndex,
    showCumulative,
    isPrediction,
    row,
  ])

  const hasIapRevenue = currentIapValue != 0 || currentPredictIapValue != 0 // 判断是否有 IAP 收入

  const hasRealData = days[`day${dayIndex}`] !== undefined
  const hasPredictData = hasPredictionAtDay(row, `day${dayIndex}`, showCumulative)
  const hasIaaData = iaaDays[`day${dayIndex}`] !== undefined
  const hasIapData = iapDays[`day${dayIndex}`] !== undefined

  // ROAS 专用预测值（仅锚点节点，直接来自 roasAcc，不通过 revenue/cost 反推）
  const predictRoasDecimal = getPredictionRoasAtDay(row, dayKey)
  const hasRoasPrediction = hasPredictionRoasAtDay(row, dayKey)

  // Roas 标签下列主行是实绩、副行可为 roasAcc；若仅有 roasAcc 而无 predictRevenue*，旧时 hasPredictData 为 false，会整块 return null，连灰字预测也看不到
  // Only hide cell if all channels (含官方 ROAS 预测)均无数据
  if (
    !hasRealData &&
    !hasPredictData &&
    !hasIaaData &&
    !hasIapData &&
    !hasRoasPrediction
  ) {
    return null
  }

  // Special color logic
  let colorClass = ''
  if (showMultiplier) {
    // No color rules for multiplier mode
    colorClass = ''
  } else if (isPrediction) {
    if (!hasRealData) {
      // No real value, use default color
      colorClass = ''
    } else {
      // Has real value, compare with prediction
      // ROAS 列优先用 roasAcc；无则降级为 revenue/cost
      const predictRoasForColor = hasRoasPrediction
        ? predictRoasDecimal
        : predictCost > 0 ? predictValue / predictCost : 0
      const realRoasForColor = cost > 0 ? value / cost : 0
      const roasDiff = Math.abs(predictRoasForColor - realRoasForColor) * 100
      
      // Use 2% threshold for Day 30 and before, 5% for after Day 30
      const threshold = dayIndex <= 30 ? 2 : 5
      
      if (roasDiff < threshold) {
        // Difference less than threshold, show green
        colorClass = 'text-green-600'
      } else {
        // Difference greater than or equal to threshold, show red
        colorClass = 'text-red-600'
      }
    }
  } else {
    const baseline = getActiveRoasBaseline(
      day0RoasLine,
      day0RoasLineTotalFallback,
      dayIndex,
      installDate,
    )
    colorClass =
      baseline == null
        ? ''
        : getColorByValue(value, cost, dayIndex, baseline, installDate)
  }

  let content
  const calculateMultiplierVal = (current, isPredict) => {
    if (multiplierDenominatorMode === 'growth_rate') {
      if (dayIndex === 0) return null
      const prev = isPredict ? (row.cumulativePredictRevenueDays?.[`day${dayIndex - 1}`] || 0) : (row.cumulativeRevenueDays?.[`day${dayIndex - 1}`] || 0)
      // fallback if summary data missing
      let prevVal = prev
      if (prevVal === 0 && !row.cumulativeRevenueDays) {
          if (showCumulative) {
              for (let i = 0; i < dayIndex; i++) {
                  prevVal += Number((isPredict ? row.predictRevenueDays : row.days)?.[`day${i}`]) || 0
              }
          } else {
              prevVal = Number((isPredict ? row.predictRevenueDays : row.days)?.[`day${dayIndex - 1}`]) || 0
          }
      }
      if (!prevVal) return null
      return (current - prevVal) / prevVal
    }
    
    let denominator = row.days?.day0 || 0
    if (multiplierDenominatorMode === 'target_roas') {
      const targetRoasD0 = getActiveRoasBaseline(
        day0RoasLine,
        day0RoasLineTotalFallback,
        0,
        row.install_date,
      )
      denominator = row.cost * targetRoasD0
    }
    if (!denominator) return null
    return current / denominator
  }

  if (showPercentage) {
    content = isPrediction ? (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasRoasPrediction ? formatPercent(predictRoasDecimal, 1, 1) : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasRealData ? formatPercent(value / cost, 1, 1) : '-'}
        </div>
      </>
    ) : (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasRealData ? formatPercent(value / cost, 1, 1) : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasRoasPrediction ? formatPercent(predictRoasDecimal, 1, 1) : '-'}
        </div>
      </>
    )
  } else if (showMultiplier) {
    const truthVal = calculateMultiplierVal(value, false)
    const predictVal = calculateMultiplierVal(predictValue, true)

    const baseline = getActiveRoasBaseline(
      day0RoasLine,
      day0RoasLineTotalFallback,
      0,
      installDate,
    )
    const realRoasColor =
      baseline == null
        ? ''
        : getColorByValue(value, cost, 0, baseline, installDate)
    const predictRoasColor =
      baseline == null
        ? ''
        : getColorByValue(predictValue, predictCost, 0, baseline, installDate)

    content = isPrediction ? (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasPredictData && predictVal !== null
            ? formatMultiplier(predictVal, 2, 2)
            : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasRealData && truthVal !== null
            ? formatMultiplier(truthVal, 2, 2)
            : '-'}
        </div>
        {dayIndex === 0 && (
          <div className={cn("text-right text-xs font-bold leading-tight", predictRoasColor)}>
            {hasPredictData ? formatPercent(predictValue / predictCost, 1, 1) : '-'}
          </div>
        )}
      </>
    ) : (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasRealData && truthVal !== null
            ? formatMultiplier(truthVal, 2, 2)
            : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasPredictData && predictVal !== null
            ? formatMultiplier(predictVal, 2, 2)
            : '-'}
        </div>
        {dayIndex === 0 && (
          <div className={cn("text-right text-xs font-bold leading-tight", realRoasColor)}>
            {hasRealData ? formatPercent(value / cost, 1, 1) : '-'}
          </div>
        )}
      </>
    )
  } else {
    content = isPrediction ? (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasPredictData ? formatUSD(predictValue) : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasRealData ? formatUSD(value) : '-'}
        </div>
      </>
    ) : (
      <>
        <div className={`text-right text-base text-black ${colorClass}`}>
          {hasRealData ? formatUSD(value) : '-'}
        </div>
        <div className="text-right text-base text-xs text-gray-500 font-normal">
          {hasPredictData ? formatUSD(predictValue) : '-'}
        </div>
      </>
    )
  }

  const tooltipContent = (
    <div className="flex flex-col text-right text-base text-black">
      <span>
        IAA:{' '}
        {showPercentage
          ? formatPercent(currentIaaValue / cost, 1, 1)
          : showMultiplier 
            ? (calculateMultiplierVal(isPrediction ? currentPredictIaaValue : currentIaaValue, isPrediction) !== null ? formatMultiplier(calculateMultiplierVal(isPrediction ? currentPredictIaaValue : currentIaaValue, isPrediction), 2, 2) : '-')
            : formatUSD(isPrediction ? currentPredictIaaValue : currentIaaValue)}
      </span>
      <span>
        IAP:{' '}
        {showPercentage
          ? formatPercent(currentIapValue / cost, 1, 1)
          : showMultiplier
            ? (calculateMultiplierVal(isPrediction ? currentPredictIapValue : currentIapValue, isPrediction) !== null ? formatMultiplier(calculateMultiplierVal(isPrediction ? currentPredictIapValue : currentIapValue, isPrediction), 2, 2) : '-')
            : formatUSD(isPrediction ? currentPredictIapValue : currentIapValue)}
      </span>
    </div>
  )

  let triangleColorClass = ''
  let triangleSizeClass = ''

  if (currentIapValue > 0) {
    triangleColorClass = 'bg-green-500'
    triangleSizeClass = 'w-[8.4px] h-[8.4px]'
  } else if (currentIapValue < 0) {
    triangleColorClass = 'bg-black'
    triangleSizeClass = 'w-[12px] h-[12px]'
  } else {
    triangleColorClass = 'bg-red-500' // 当 iapValue 为 0 但仍有 hasIapRevenue (例如预测值) 时
    triangleSizeClass = 'w-[8.4px] h-[8.4px]'
  }

  const cellContent = (
    <div className="relative h-full w-full">
      {content}
      {hasIapRevenue && (
        <div
          className={`absolute -top-2 -right-2 ${triangleSizeClass} ${triangleColorClass}`}
          style={{ clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%)' }}
        ></div>
      )}
    </div>
  )

  if (hasIapRevenue) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
          <TooltipContent>{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return cellContent
}

const RoasHeader = ({ revisedRoas }) => (
  <div className="flex flex-col">
    <div className="flex items-center justify-end gap-1 font-bold">
      <span className="text-base text-black">ROAS</span>
    </div>
    {/* {revisedRoas?.summary?.roasline && (
      <div className="text-right text-xs text-gray-500">
        {formatPercent(revisedRoas.summary.roasline, 2, 2)}
      </div>
    )}
    {revisedRoas?.summary?.roiline && (
      <div className="text-right text-xs text-red-600">
        {formatPercent(revisedRoas.summary.roiline, 2, 2)}
      </div>
    )} */}
  </div>
)

const DayNHeader = ({
  dayIndex,
  day0RoasLine,
  day0RoasLineTotalFallback,
  mediaSource,
  handlerDayNClick,
  average,
  sortedData,
  sorts,
  handleSort,
  displayMode,
  dayMode,
  isPrediction,
}) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  // Channel-owned latest date for this campaign; ignore newer total-fallback dates.
  const campaignLatestDate = useMemo(() => {
    if (!day0RoasLine || typeof day0RoasLine !== 'object') return ''
    const dayData = day0RoasLine[dayIndex]
    if (!dayData || typeof dayData !== 'object') return ''

    const ownedDates = Object.keys(dayData).filter(
      (date) => !day0RoasLineTotalFallback?.[dayIndex]?.[date],
    )
    const dates = ownedDates.length > 0 ? ownedDates : Object.keys(dayData)
    return dates.reduce((max, date) => (date > max ? date : max), '')
  }, [day0RoasLine, day0RoasLineTotalFallback, dayIndex])

  // 获取 baseline 历史记录用于 tooltip 显示
  const baselineHistory = useMemo(() => {
    if (!day0RoasLine || typeof day0RoasLine !== 'object') return []
    const dayData = day0RoasLine[dayIndex]
    if (!dayData || typeof dayData !== 'object') return []
    return Object.entries(dayData)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => b.date.localeCompare(a.date))
  }, [day0RoasLine, dayIndex])

  // 获取最新的可见日期
  let latestDate = null
  if (visibleDates.size > 0) {
    // 将 YYYYMMDD 格式转换为 YYYY-MM-DD
    const latestDateStr = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
    if (latestDateStr && latestDateStr.length === 8) {
      latestDate = `${latestDateStr.substring(0, 4)}-${latestDateStr.substring(4, 6)}-${latestDateStr.substring(6, 8)}`
    }
  } else if (sortedData && sortedData.length > 0) {
    // 如果没有可见日期，使用所有行的最新日期
    latestDate = sortedData.reduce((maxDate, row) => {
      const currentDate = row.install_date || ''
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const baselineResolution = useMemo(() => {
    if (day0RoasLine && latestDate) {
      return resolveRoasBaseline(
        day0RoasLine,
        day0RoasLineTotalFallback,
        dayIndex,
        latestDate,
      )
    }

    if (dayIndex === 0 && typeof day0RoasLine === 'number') {
      return { value: day0RoasLine, isTotalFallback: false }
    }

    return { value: null, isTotalFallback: false }
  }, [day0RoasLine, day0RoasLineTotalFallback, dayIndex, latestDate])

  // Effective current startDate: channel-owned preferred over newer total fallback.
  const currentBaselineStartDate =
    baselineResolution.startDate || campaignLatestDate

  const baselineValue =
    TOTAL_FALLBACK_STANDARD_MODE === 'hide' &&
    baselineResolution.isTotalFallback
      ? null
      : baselineResolution.value

  // Green: channel-owned effective standard. Blue: total fallback.
  // Do not compare against cross-media global latest — newer total must not
  // make a valid channel standard look stale.
  const baselineClassName = baselineResolution.isTotalFallback
    ? 'text-blue-600 font-semibold'
    : baselineValue != null
      ? 'text-green-600 font-semibold'
      : 'text-black'

  // 获取当前列的排序状态
  const sortCode = `day${dayIndex}`
  const currentSort = sorts?.find((s) => s.code === sortCode)
  const isAsc = currentSort?.order === 'asc'
  const isDesc = currentSort?.order === 'desc'

  const isD0Multiplier = dayIndex === 0 && displayMode === 'multiplier'
  const roasSortCode = 'day0_roas'
  const currentRoasSort = sorts?.find((s) => s.code === roasSortCode)

  const handleSortRequest = (code, order) => {
    if (!code) {
      // 移除当前列的所有相关排序
      const newSorts = sorts.filter((s) => {
        if (s.code === sortCode || s.code === roasSortCode) return false
        if (s.code.startsWith('day') && /^day\d+$/.test(s.code)) return false
        return true
      })
      if (newSorts.length === 0) {
        handleSort([{ code: 'install_date', order: 'desc' }])
      } else {
        handleSort(newSorts)
      }
      return
    }

    // 设置新的排序，先清除所有其他 DayN 列的排序
    const nonDayNSorts = sorts.filter((s) => {
      if (s.code === sortCode || s.code === roasSortCode) return false
      if (s.code.startsWith('day') && /^day\d+$/.test(s.code)) return false
      return true
    })
    handleSort([{ code, order }, ...nonDayNSorts])
  }

  // 处理排序点击
  const handleSortClick = (e) => {
    e.stopPropagation() // 阻止触发图表点击事件
    
    let newOrder
    if (!currentSort) {
      // 默认 -> 降序
      newOrder = 'desc'
    } else if (isDesc) {
      // 降序 -> 升序
      newOrder = 'asc'
    } else {
      // 升序 -> 默认（移除排序）
      newOrder = null
    }

    handleSortRequest(newOrder ? sortCode : null, newOrder)
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center text-base text-black gap-1">
        {isD0Multiplier ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center justify-center p-0.5 hover:bg-gray-100 rounded transition-colors"
                title="Sorting Options"
                onClick={(e) => e.stopPropagation()}
              >
                {(isAsc || currentRoasSort?.order === 'asc') && <ArrowUp className="w-4 h-4 text-primary" />}
                {(isDesc || currentRoasSort?.order === 'desc') && <ArrowDown className="w-4 h-4 text-primary" />}
                {!isAsc && !isDesc && !currentRoasSort && (
                  <ArrowUpDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleSortRequest(sortCode, 'desc')}>
                倍率(倒序) {isDesc && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSortRequest(sortCode, 'asc')}>
                倍率(正序) {isAsc && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSortRequest(roasSortCode, 'desc')}>
                Roas(倒序) {currentRoasSort?.order === 'desc' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSortRequest(roasSortCode, 'asc')}>
                Roas(正序) {currentRoasSort?.order === 'asc' && '✓'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSortRequest(null)}>
                取消排序
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            onClick={handleSortClick}
            className="flex items-center justify-center p-0.5 hover:bg-gray-100 rounded transition-colors"
            title={isAsc ? '升序' : isDesc ? '降序' : '默认'}
          >
            {isAsc && <ArrowUp className="w-4 h-4" />}
            {isDesc && <ArrowDown className="w-4 h-4" />}
            {!isAsc && !isDesc && (
              <ArrowUpDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
        )}
        <div
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => handlerDayNClick(dayIndex)}
        >
          <span>D{dayIndex}</span>
          <LineChartIcon className="w-4 h-4" />
        </div>
      </div>
      {baselineValue && displayMode !== 'multiplier' ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-right text-xs text-gray-500 cursor-help">
                <span className={baselineClassName}>
                  {formatPercent(baselineValue, 2, 2)}
                </span>
                /{average}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {baselineHistory.length > 0 ? (
                <div className="space-y-1">
                  <p className="font-medium">Baseline History:</p>
                  {baselineHistory.map((record) => (
                    <p key={record.date} className="text-xs">
                      {record.date}: {formatPercent(record.value, 0, 0)}
                      <span className="text-muted-foreground">
                        {' '}Channel: {getRoasBaselineSource(
                          day0RoasLineTotalFallback,
                          dayIndex,
                          record.date,
                          mediaSource,
                        )}
                      </span>
                      {record.date === currentBaselineStartDate && ' (current)'}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs">No baseline configuration found</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-right text-xs text-gray-500">{average}</span>
      )}
    </div>
  )
}

const getCumulativePrediction = (predictDays, targetDay) => {
  if (!predictDays) return 0
  let total = 0
  for (let i = 0; i <= targetDay; i++) {
    total += (predictDays[`day${i}`] || 0)
  }
  return total
}

const RoasTable = (props) => {
  const {
    roas,
    compareRoas,
    revisedRoas,
    day0RoasLine,
    day0RoasLineTotalFallback,
    isTotal = false,
    simplifiedMode,
    campaignId,
    targetDays,
    isPrediction = false,
    summaryMode,
    handleRefrech,
  } = props

  const tableRef = useRef(null)
  const [isChartModalOpen, setIsChartModalOpen] = useState(false)
  const [chartDayIndex, setChartDayIndex] = useState(null)
  const [sorts, setSorts] = useState([{ code: 'install_date', order: 'desc' }])
  const { 
    weekFilter, 
    displayMode, 
    dayMode, 
    dayOrder,
    viewMode,
    multiplierDenominatorMode,
    setDisplayMode,
    setDayMode,
    setMultiplierDenominatorMode
  } = useRoasStore()

  // 获取 campaign 详情用于 Benchmark 报告标题
  const { data: campaignDetail } = useSWR(
    campaignId ? [`/campaigns/${campaignId}`] : null,
    fetcher
  )

  const benchmarkSourceInfo = useMemo(() => {
    if (!campaignDetail) return { appName: 'Analysis' }
    // 使用 pid 获取正确的 app 名称
    const appName = campaignDetail.pid
      ? obtainAppNameByPid(campaignDetail.pid) || campaignDetail.name || 'Analysis'
      : campaignDetail.name || 'Analysis'
    return {
      appName,
      country: campaignDetail.country || '',
      packageName: campaignDetail.packageName || '',
    }
  }, [campaignDetail])

  // 检查是否有目标 ROAS 基准线
  const hasTargetRoas = useMemo(() => {
    return hasUsableTargetRoas(day0RoasLine, day0RoasLineTotalFallback)
  }, [day0RoasLine, day0RoasLineTotalFallback])

  // 如果当前模式是目标 ROAS 但数据不存在，自动回退到 D0 Revenue
  useEffect(() => {
    if (multiplierDenominatorMode === 'target_roas' && !hasTargetRoas) {
      setMultiplierDenominatorMode('d0_revenue')
    }
  }, [multiplierDenominatorMode, hasTargetRoas, setMultiplierDenominatorMode])

  const handleSort = useCallback(

    (newSorts) => {
      setSorts(newSorts)
    },
    [],
  )

  // 获取 DayN 列的原始数值用于排序
  const getDayNumericValue = useCallback(
    (row, dayIndex, forcedMode) => {
      const mode = forcedMode || displayMode
      const showPercentage = mode === 'percentage'
      const showMultiplier = mode === 'multiplier'
      const showCumulative = isPrediction ? true : dayMode === 'cumulative'
      const days = row.days || {}
      const predictDays = row.predictRevenueDays || {}
      
      const dayKey = `day${dayIndex}`
      const cost = row.dayCosts?.[dayKey] || row.cost || 0
      const predictCost = row.predictDayCosts?.[dayKey] || row.cost || 0
      
      const getRev = (dIndex, isPredict) => {
        if (dIndex < 0) return 0
        const dKey = `day${dIndex}`
        const rDays = isPredict ? row.predictRevenueDays : row.days
        if (showCumulative) {
          if (isPredict) {
            return getPredictionRevenueAtDay(row, dKey, true)
          }
          if (row.cumulativeRevenueDays) {
            return row.cumulativeRevenueDays[dKey] || 0
          }
          let sum = 0
          for (let i = 0; i <= dIndex; i++) {
            sum += Number((row.days || {})[`day${i}`]) || 0
          }
          return sum
        }
        return Number(rDays?.[dKey]) || 0
      }

      const hasRealData = days[`day${dayIndex}`] !== undefined
      const hasPredictData = hasPredictionAtDay(row, dayKey, showCumulative)

      // 预测 Tab + %：主栏与 DayCell 一致，优先官方 roasAcc；无收入预测但只有 roasAcc 时仍参与排序
      const pctMode = (forcedMode || displayMode) === 'percentage'
      if (isPrediction && pctMode) {
        const roasAccEarly = getPredictionRoasAtDay(row, dayKey)
        if (roasAccEarly != null) return Number(roasAccEarly)
      }

      if (!hasRealData && !hasPredictData) {
        return null 
      }

      let value = 0
      let currentCost = 0
      if (isPrediction) {
        if (hasPredictData) {
          value = getRev(dayIndex, true)
          currentCost = predictCost
        } else {
          return null
        }
      } else {
        if (hasRealData) {
          value = getRev(dayIndex, false)
          currentCost = cost
        } else {
          return null
        }
      }

      if (showPercentage) {
        // ROAS 列：预测模式优先读 roasAcc 直接值，避免用 revenue/cost 反推
        if (isPrediction) {
          const roasAccDirect = getPredictionRoasAtDay(row, dayKey)
          if (roasAccDirect != null) return Number(roasAccDirect)
        }
        return currentCost ? value / currentCost : null
      }

      if (showMultiplier) {
        if (multiplierDenominatorMode === 'growth_rate') {
          if (dayIndex === 0) return null
          const prevValue = getRev(dayIndex - 1, isPrediction)
          if (!prevValue) return null
          return (value - prevValue) / prevValue
        }
        
        let denominator = row.days?.day0 || 0
        if (multiplierDenominatorMode === 'target_roas') {
          const targetRoasD0 = getActiveRoasBaseline(
            day0RoasLine,
            day0RoasLineTotalFallback,
            0,
            row.install_date,
          )
          denominator = row.cost * targetRoasD0
        }
        
        if (denominator) {
          return value / denominator
        } else {
          return null
        }
      }

      return value
    },
    [
      displayMode,
      dayMode,
      isPrediction,
      multiplierDenominatorMode,
      day0RoasLine,
      day0RoasLineTotalFallback,
    ],
  )
  
  const sortedData = useMemo(() => {
    let sorted

    // Helper to sort data based on current sorts
    const applySort = (data) => {
      if (!sorts || sorts.length === 0) {
        return baseSortedData(data, weekFilter, sorts)
      }

      const hasDayNSort = sorts.some(
        (s) =>
          (s.code.startsWith('day') && /^day\d+$/.test(s.code)) ||
          s.code === 'day0_roas' ||
          /^\d{4}-\d{2}-\d{2}$/.test(s.code),
      )

      if (!hasDayNSort) {
        return baseSortedData(data, weekFilter, sorts)
      }

      return [...data].sort((a, b) => {
        for (const sortItem of sorts) {
          const { code, order } = sortItem

          if (code === 'day0_roas') {
            const aValue = getDayNumericValue(a, 0, 'percentage')
            const bValue = getDayNumericValue(b, 0, 'percentage')

            if (aValue === null && bValue === null) continue
            if (aValue === null) return 1
            if (bValue === null) return -1
            if (aValue === bValue) continue

            if (order === 'asc') {
              return aValue > bValue ? 1 : -1
            } else {
              return aValue < bValue ? 1 : -1
            }
          }

          if (code.startsWith('day') && /^day\d+$/.test(code)) {
            const dayIndex = parseInt(code.replace('day', ''), 10)
            const aValue = getDayNumericValue(a, dayIndex)
            const bValue = getDayNumericValue(b, dayIndex)

            if (aValue === null && bValue === null) continue
            if (aValue === null) return 1
            if (bValue === null) return -1
            if (aValue === bValue) continue

            if (order === 'asc') {
              return aValue > bValue ? 1 : -1
            } else {
              return aValue < bValue ? 1 : -1
            }
          }

          if (/^\d{4}-\d{2}-\d{2}$/.test(code)) {
            // Calendar date column sorting
            const getCalendarValue = (row) => {
              if (!row.install_date) return null
              const dayIndex = dayjs(code).diff(dayjs(row.install_date), 'day')
              if (dayIndex < 0) return null
              return getDayNumericValue(row, dayIndex)
            }
            const aValue = getCalendarValue(a)
            const bValue = getCalendarValue(b)

            if (aValue === null && bValue === null) continue
            if (aValue === null) return 1
            if (bValue === null) return -1
            if (aValue === bValue) continue

            if (order === 'asc') {
              return aValue > bValue ? 1 : -1
            } else {
              return aValue < bValue ? 1 : -1
            }
          }

          let aValue = a[code]
          let bValue = b[code]

          if (code === 'install_date') {
            aValue = String(aValue).replace(/-/g, '')
            bValue = String(bValue).replace(/-/g, '')
          }

          if (aValue === bValue) continue

          if (order === 'asc') {
            return aValue > bValue ? 1 : -1
          } else {
            return aValue < bValue ? 1 : -1
          }
        }
        return 0
      })
    }

    const mainSorted = applySort(roas.dailyData)

    if (compareRoas && (Array.isArray(compareRoas) ? compareRoas.length > 0 : compareRoas.dailyData)) {
      const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : [compareRoas]
      // 按 install_date 匹配的 map（用于「其他 campaign 同日期」对比）
      const compareDataMaps = compareCampaigns.map((c) => {
        if (c.isDateCompare) return null // 日期对比在下面按索引取
        const map = new Map()
        c?.dailyData?.forEach((row) => {
          map.set(row.install_date, row)
        })
        return map
      })
      // 同 campaign 不同日期对比：按与主表相同的排序，按行索引对齐
      const sortedCompareByIndex = compareCampaigns.map((c) =>
        c.isDateCompare && c.dailyData?.length ? applySort(c.dailyData) : null,
      )

      const combined = []
      let mainRowIndex = 1
      mainSorted.forEach((row, mainIndex) => {
        combined.push({
          ...row,
          type: 'main',
          rowId: `${row.install_date}_main`,
          index: mainRowIndex,
        })

        compareCampaigns.forEach((c, idx) => {
          const compareRow = c.isDateCompare
            ? sortedCompareByIndex[idx]?.[mainIndex] ?? null
            : compareDataMaps[idx]?.get(row.install_date) ?? null
          if (compareRow) {
            combined.push({
              ...compareRow,
              type: 'compare',
              compareIndex: idx,
              rowId: c.isDateCompare ? `idx${mainIndex}_compare_${idx}` : `${row.install_date}_compare_${idx}`,
              campaignName: c.campaignName || 'Compare',
            })
            if (!c.isDateCompare && compareDataMaps[idx]) {
              compareDataMaps[idx].delete(row.install_date)
            }
          }
        })
        mainRowIndex++
      })

      // Handle leftovers（仅非日期对比：主表没有的日期作为剩余行追加；日期对比已按索引对齐，无 leftovers）
      compareDataMaps.forEach((map, idx) => {
        if (!map) return
        const leftovers = Array.from(map.values()).sort((a, b) =>
          b.install_date.localeCompare(a.install_date),
        )
        leftovers.forEach((row) => {
          // Check if this date was already added by a previous compare campaign's leftover or main
          // Actually, leftovers are dates NOT in main. But they might be in multiple compare campaigns.
          // To keep it simple and consistent with interleaved style, we'll just append them.
          // But wait, if multiple compare campaigns have the same date that main doesn't have, 
          // they should probably be grouped together.
          
          // Better logic: find if we already added a row for this date in 'combined'
          const existingDateIndex = combined.findLastIndex(r => r.install_date === row.install_date)
          if (existingDateIndex !== -1) {
            combined.splice(existingDateIndex + 1, 0, {
              ...row,
              type: 'compare',
              compareIndex: idx,
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          } else {
            combined.push({
              ...row,
              type: 'compare',
              compareIndex: idx,
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          }
        })
      })

      return combined
    }

    // Normal mode: add unique rowId and index
    return mainSorted.map((row, index) => ({
      ...row,
      type: 'main',
      rowId: `${row.install_date}_main`,
      index: index + 1,
    }))
  }, [roas.dailyData, compareRoas, weekFilter, sorts, getDayNumericValue])

  const dateRange = useMemo(() => {
    if (viewMode !== 'calendar' || !sortedData || sortedData.length === 0) {
      return []
    }

    // Get min and max dates from sortedData
    const dates = sortedData
      .filter((row) => row.type === 'main' && row.install_date)
      .map((row) => row.install_date)

    if (dates.length === 0) return []

    const minDate = dates.reduce((min, d) => (d < min ? d : min), dates[0])
    const maxDate = dayjs().format('YYYY-MM-DD') // Use today as max date

    const range = []
    let current = dayjs(minDate)
    const end = dayjs(maxDate)

    while (current.isBefore(end) || current.isSame(end, 'day')) {
      range.push(current.format('YYYY-MM-DD'))
      current = current.add(1, 'day')
    }

    return range
  }, [viewMode, sortedData])

  const dayIndices = useMemo(() => {
    let indices = []
    if (targetDays) {
      indices = targetDays
    } else {
      const maxDay = Math.max(roas.dailyData.length, 34)
      const useSimplified = simplifiedMode ?? isTotal
      indices = useSimplified
        ? TargetDays
        : Array.from({ length: maxDay }, (_, i) => i)
    }
    return dayOrder === 'desc' ? [...indices].reverse() : indices
  }, [targetDays, isTotal, simplifiedMode, roas.dailyData.length, dayOrder])

  const columnAverages = useMemo(() => {
    const averages = {}
    const showPercentage = displayMode === 'percentage'
    const showMultiplier = displayMode === 'multiplier'
    const showCumulative = isPrediction ? true : dayMode === 'cumulative'

    // Sort by date descending to get recent data
    const recentRows = [...sortedData].sort((a, b) =>
      (b.install_date || '').localeCompare(a.install_date || ''),
    )

    const calculateAverage = (dayIndex) => {
      let totalNum = 0
      let totalDenom = 0
      let totalPrevNum = 0
      let count = 0

      for (const row of recentRows) {
        if (count >= 30) break

        const getRev = (dIndex, isPredict) => {
          if (dIndex < 0) return 0
          const dKey = `day${dIndex}`
          if (showCumulative) {
            if (isPredict) {
              return getPredictionRevenueAtDay(row, dKey, true)
            }
            if (row.cumulativeRevenueDays) {
              return row.cumulativeRevenueDays[dKey] || 0
            }
            let sum = 0
            for (let i = 0; i <= dIndex; i++) {
              sum += Number((row.days || {})[`day${i}`]) || 0
            }
            return sum
          }
          return Number((isPredict ? row.predictRevenueDays : row.days)?.[dKey]) || 0
        }

        const days = row.days || {}
        const predictDays = row.predictRevenueDays || {}
        const dayKey = `day${dayIndex}`
        
        const hasRealData = days[dayKey] !== undefined
        const hasPredictData = hasPredictionAtDay(row, dayKey, showCumulative)
        const hasRoasPred = hasPredictionRoasAtDay(row, dayKey)
        // 预测列：% 可仅 roasAcc；倍率/金额仍仅收入预测（与 DayCell）
        const isValid = isPrediction
          ? showPercentage
            ? hasPredictData || hasRoasPred
            : hasPredictData
          : hasRealData

        if (!isValid) continue

        const val = getRev(dayIndex, isPrediction)
        const currentCost = isPrediction ? (row.predictDayCosts?.[dayKey] || row.cost) : (row.dayCosts?.[dayKey] || row.cost)

        if (showPercentage) {
          // 预测模式：优先用 roasAcc 直接值做加权平均，避免 revenue/cost 反推偏差
          const predRoasAcc = isPrediction ? getPredictionRoasAtDay(row, dayKey) : undefined
          if (isPrediction && predRoasAcc != null) {
            if (currentCost) {
              totalNum += Number(predRoasAcc) * currentCost
              totalDenom += currentCost
              count++
            }
          } else if (currentCost) {
            totalNum += val
            totalDenom += currentCost
            count++
          }
        } else if (showMultiplier) {
          if (multiplierDenominatorMode === 'growth_rate') {
            if (dayIndex > 0) {
              const prevVal = getRev(dayIndex - 1, isPrediction)
              totalNum += val
              totalPrevNum += prevVal
              count++
            }
          } else if (multiplierDenominatorMode === 'target_roas') {
            const targetRoasD0 = getActiveRoasBaseline(
              day0RoasLine,
              day0RoasLineTotalFallback,
              0,
              row.install_date,
            )
            const denom = row.cost * targetRoasD0
            if (denom) {
              totalNum += val
              totalDenom += denom
              count++
            }
          } else {
            // d0_revenue
            const denom = row.days?.day0 || 0
            if (denom) {
              totalNum += val
              totalDenom += denom
              count++
            }
          }
        } else {
          totalNum += val
          count++
        }
      }

      if (showPercentage) {
        return totalDenom ? formatPercent(totalNum / totalDenom, 2, 2) : '-'
      } else if (showMultiplier) {
        if (multiplierDenominatorMode === 'growth_rate') {
          return totalPrevNum ? formatMultiplier(totalNum / totalPrevNum, 2, 2) : '-'
        }
        return totalDenom ? formatMultiplier(totalNum / totalDenom, 2, 2) : '-'
      } else {
        return count ? formatUSD(totalNum / count) : '-'
      }
    }

    if (viewMode === 'calendar') {
      dateRange.forEach((date) => {
        averages[date] = '-'
      })
    } else {
      dayIndices.forEach((dayIndex) => {
        averages[dayIndex] = calculateAverage(dayIndex)
      })
    }
    return averages
  }, [
    sortedData,
    dayIndices,
    dateRange,
    displayMode,
    dayMode,
    isPrediction,
    viewMode,
    day0RoasLine,
    day0RoasLineTotalFallback,
    multiplierDenominatorMode,
  ])

  const handlerDayNClick = (dayIndex) => {
    setIsChartModalOpen(true)
    setChartDayIndex(dayIndex)
  }

  const getDayCellValue = useCallback(
    (row, dayIndex) => {
      const showPercentage = displayMode === 'percentage'
      const showMultiplier = displayMode === 'multiplier'
      const showCumulative = isPrediction ? true : dayMode === 'cumulative'
      const days = row.days || {}
      const predictDays = row.predictRevenueDays || {}
      
      const dayKey = `day${dayIndex}`
      const cost = row.dayCosts?.[dayKey] || row.cost || 0
      const predictCost = row.predictDayCosts?.[dayKey] || row.cost || 0

      const getRev = (dIndex, isPredict) => {
        if (dIndex < 0) return 0
        const dKey = `day${dIndex}`
        const rDays = isPredict ? row.predictRevenueDays : row.days
        if (showCumulative) {
          if (isPredict) {
            return getPredictionRevenueAtDay(row, dKey, true)
          }
          if (row.cumulativeRevenueDays) {
            return row.cumulativeRevenueDays[dKey] || 0
          }
          let sum = 0
          for (let i = 0; i <= dIndex; i++) {
            sum += Number((row.days || {})[`day${i}`]) || 0
          }
          return sum
        }
        return Number(rDays?.[dKey]) || 0
      }

      const hasRealData = days[`day${dayIndex}`] !== undefined
      const hasPredictData = hasPredictionAtDay(row, dayKey, showCumulative)
      const iaaDays = row.iaa || {}
      const iapDays = row.iap || {}
      const hasIaaData = iaaDays[`day${dayIndex}`] !== undefined
      const hasIapData = iapDays[`day${dayIndex}`] !== undefined

      if (
        !hasRealData &&
        !hasPredictData &&
        !hasIaaData &&
        !hasIapData &&
        !(isPrediction && hasPredictionRoasAtDay(row, dayKey))
      ) {
        return ''
      }

      if (showPercentage) {
        if (isPrediction) {
          // 优先读 roasAcc 直接值（与界面显示一致）
          const roasAccDirect = getPredictionRoasAtDay(row, dayKey)
          if (roasAccDirect != null) return formatPercent(Number(roasAccDirect), 1, 1)
          if (!hasPredictData || !predictCost) return '-'
          return formatPercent(getRev(dayIndex, true) / predictCost, 1, 1)
        }
        if (!hasRealData || !cost) {
          return '-'
        }
        return formatPercent(getRev(dayIndex, false) / cost, 1, 1)
      }

      if (showMultiplier) {
        const calculateVal = (isPredict) => {
          if (multiplierDenominatorMode === 'growth_rate') {
            if (dayIndex === 0) return null
            const current = getRev(dayIndex, isPredict)
            const prev = getRev(dayIndex - 1, isPredict)
            if (!prev) return null
            return (current - prev) / prev
          }
          
          let denominator = row.days?.day0 || 0
          if (multiplierDenominatorMode === 'target_roas') {
            const targetRoasD0 = getActiveRoasBaseline(
              day0RoasLine,
              day0RoasLineTotalFallback,
              0,
              row.install_date,
            )
            denominator = row.cost * targetRoasD0
          }
          if (!denominator) return null
          return getRev(dayIndex, isPredict) / denominator
        }

        if (isPrediction) {
          const val = calculateVal(true)
          return val === null ? '-' : formatMultiplier(val, 2, 2)
        }
        const val = calculateVal(false)
        return val === null ? '-' : formatMultiplier(val, 2, 2)
      }

      if (isPrediction) {
        if (!hasPredictData) {
          return '-'
        }
        return formatUSD(getRev(dayIndex, true))
      }
      if (!hasRealData) {
        return '-'
      }
      return formatUSD(getRev(dayIndex, false))
    },
    [
      displayMode,
      dayMode,
      isPrediction,
      multiplierDenominatorMode,
      day0RoasLine,
      day0RoasLineTotalFallback,
    ],
  )

  const handleExportCsv = useCallback(() => {
    if (!sortedData || sortedData.length === 0) {
      return
    }

    const toFixedNumber = (value, digits = 2) => {
      if (value === undefined || value === null) {
        return ''
      }
      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) {
        return ''
      }
      return Number(numericValue.toFixed(digits))
    }

    const filename = `roas_daily_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    const exportRows = sortedData.map((row) => {
      const exportRow = {}

      exportRow['Install Date'] = row.install_date
        ? Number(row.install_date.replaceAll('-', ''))
        : ''

      const revenueValue = isPrediction
        ? getPredictionRevenueAtDay(row, 'day150', true)
        : row.revenue
      exportRow.Revenue = toFixedNumber(revenueValue)

      exportRow.Cost = toFixedNumber(row.cost)

      const profitRevenue = isPrediction ? revenueValue : row.revenue
      const profit =
        profitRevenue === undefined ||
        profitRevenue === null ||
        row.cost === undefined ||
        row.cost === null
          ? undefined
          : profitRevenue - row.cost
      exportRow.Profit = toFixedNumber(profit)

      exportRow.Installs =
        row.installs === undefined || row.installs === null ? '' : row.installs

      const cpi =
        row.installs && row.installs !== 0 ? row.cost / row.installs : undefined
      exportRow.CPI = toFixedNumber(cpi, 4)

      if (!isPrediction) {
        exportRow.ROAS = row.roas ? formatPercent(row.roas / 100, 2, 2) : '-'
      }

      if (!isPrediction) {
        const totalRevenue = Object.values(row.days || {}).reduce(
          (acc, value) => acc + (value || 0),
          0,
        )
        const ltv =
          row.installs && row.installs !== 0
            ? totalRevenue / row.installs
            : undefined
        exportRow.LTV = toFixedNumber(ltv, 4)
      }

      if (viewMode === 'calendar') {
        dateRange.forEach((date) => {
          const dayIndex = dayjs(date).diff(dayjs(row.install_date), 'day')
          if (dayIndex >= 0) {
            exportRow[date] = getDayCellValue(row, dayIndex)
          } else {
            exportRow[date] = ''
          }
        })
      } else {
        dayIndices.forEach((dayIndex) => {
          const columnLabel = `Day ${dayIndex}`
          exportRow[columnLabel] = getDayCellValue(row, dayIndex)
        })
      }

      return exportRow
    })

    exportToCsv(exportRows, filename)
  }, [sortedData, isPrediction, isTotal, dayIndices, dateRange, viewMode, getDayCellValue])

  const columns = useMemo(() => {
    // 创建基于排序后数据的 IndexColumn
    // 直接使用数据行中的 index 字段，该字段已在原始数据上绑定，排序后已更新
    const sortedIndexColumn = {
      ...IndexColumn,
      render: (value, row) => {
        // Only show index for main rows
        if (row.type === 'compare') return null
        return (
          <div className="text-right text-base text-black">{row.index}</div>
        )
      },
    }

    // Helper for compare row styling
    const renderCell = (content, row) => {
      if (row.type === 'compare') {
        return <div className="text-muted-foreground italic">{content}</div>
      }
      return content
    }

    const baseColumns = [
      sortedIndexColumn,
      {
        ...InstallDateColumn(
          roas.campaignId,
          handleSort,
          sorts,
          day0RoasLine,
          day0RoasLineTotalFallback,
          compareRoas,
          summaryMode,
          roas.dailyData,
          (row) => (row.type === 'main' ? row.index - 1 : undefined),
        ),
        render: (value, row) => {
          if (row.type === 'compare') {
            const dateLabel = row.displayDate || row.install_date || ''
            const campaignShort = row.campaignName ? shortCampaignLabel(row.campaignName) : ''
            const label = campaignShort ? `${dateLabel} · ${campaignShort}` : dateLabel
            return (
              <div className="pl-5 text-muted-foreground text-sm flex items-center h-full italic" title={row.campaignName || dateLabel}>
                {label}
              </div>
            )
          }
          return (
            <div className="relative">
              {InstallDateColumn(
                roas.campaignId,
                handleSort,
                sorts,
                day0RoasLine,
                day0RoasLineTotalFallback,
                compareRoas,
                summaryMode,
                roas.dailyData,
                (r) => (r.type === 'main' ? r.index - 1 : undefined),
              ).render(value, row)}
            </div>
          )
        },
      },
      {
        ...RevenueColumn,
        lock: true,
        render: (value, row) => {
          if (isPrediction) {
            const day150Revenue = getCumulativePrediction(
              row.predictRevenueDays,
              150,
            )
            return renderCell(<MoneyCell value={day150Revenue} />, row)
          }
          return renderCell(<MoneyCell value={value} />, row)
        },
      },
      {
        code: 'cost',
        name: <span className="text-base text-black">Cost</span>,
        width: 140,
        align: 'right',
        lock: true,
        render: (value, row) => {
          if (isTotal) {
            return renderCell(<MoneyCell value={value} />, row)
          } else {
            return renderCell(
              <CostCell cost={value} totalCost={row.totalCampCost} />,
              row,
            )
          }
        },
      },
      {
        code: 'profit',
        name: <span className="text-base text-black">Profit</span>,
        width: 120,
        align: 'right',
        render: (value, row) => {
          if (isPrediction) {
            const day150Revenue = getCumulativePrediction(
              row.predictRevenueDays,
              150,
            )
            return renderCell(
              <ProfitCell revenue={day150Revenue} cost={row.cost} />,
              row,
            )
          }
          return renderCell(
            <ProfitCell revenue={row.revenue} cost={row.cost} />,
            row,
          )
        },
      },
      {
        ...InstallsColumn(isTotal),
        render: (value, row) =>
          renderCell(InstallsColumn(isTotal).render(value, row), row),
      },
      {
        code: 'cpi',
        name: <span className="text-base text-black">CPI</span>,
        width: 100,
        align: 'right',
        render: (value, row) => {
          const cpi = row.cost / row.installs
          return renderCell(
            <div className="text-right text-base text-black">
              {formatUSD(cpi, 4, 4)}
            </div>,
            row,
          )
        },
      },
      {
        code: 'roas',
        name: <RoasHeader revisedRoas={revisedRoas} />,
        width: 100,
        align: 'right',
        render: (value, row) => {
          const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : (compareRoas ? [compareRoas] : [])
          const matchingDay = (
            row.type === 'compare' 
              ? compareCampaigns[row.compareIndex] 
              : revisedRoas
          )?.dailyData?.find((item) => (item.date || item.install_date) === row.install_date)

          return renderCell(
            <RoasCell
              value={row.roas}
              roasline={matchingDay?.roasline}
              roiline={matchingDay?.roiline}
            />,
            row,
          )
        },
      },
      {
        code: 'day90Roas',
        name: (
          <div className="flex flex-col items-end">
            <span className="text-base text-black">ROAS</span>
            <span className="text-xs text-gray-500">(90-total)</span>
          </div>
        ),
        width: 100,
        align: 'right',
        render: (value, row) => {
          const day90Roas = row.total_d90_roas
          if (!day90Roas) {
            return (
              <div className="text-right text-base text-black">
                <span className="text-muted-foreground">-</span>
              </div>
            )
          }
          // day90 值已经是百分比格式（乘以100后的值），所以直接除以100格式化
          return renderCell(
            <div className="text-right text-base text-black">
              {formatPercent(day90Roas / 100, 2, 2)}
            </div>,
            row,
          )
        },
      },
      {
        code: 'spacer',
        name: '',
        width: 20,
        render: () => <div className="w-2"></div>,
      },
      {
        code: 'LTV',
        title: <span className="text-base text-black">LTV</span>,
        width: 100,
        align: 'right',
        render: (value, row) =>
          renderCell(<LtvCell row={row} historyDay={4} />, row),
      },
      {
        code: 'spacer1',
        name: '',
        width: 20,
        render: () => <div className="w-2"></div>,
      },
    ]

    const filteredBaseColumns = baseColumns.filter((col) => {
      if (
        isPrediction &&
        (col.code === 'roas' || col.code === 'day90Roas' || col.code === 'LTV')
      )
        return false
      // Day90Roas 列只在 isTotal 为 true 时展示
      if (col.code === 'day90Roas' && !isTotal) return false
      return true
    })

    // Add day columns
    const lifecycleColumns = dayIndices.map((dayIndex) => ({
      code: `day${dayIndex}`,
      name: (
        <DayNHeader
          dayIndex={dayIndex}
          day0RoasLine={day0RoasLine}
          day0RoasLineTotalFallback={day0RoasLineTotalFallback}
          mediaSource={roas?.roasLineMediaSource || roas?.mediaSource}
          handlerDayNClick={handlerDayNClick}
          average={columnAverages[dayIndex]}
          sortedData={sortedData}
          sorts={sorts}
          handleSort={handleSort}
          displayMode={displayMode}
          dayMode={dayMode}
          isPrediction={isPrediction}
        />
      ),
      width: 120,
      align: 'right',
      render: (value, row) =>
        renderCell(
          <DayCell
            dayIndex={dayIndex}
            row={row}
            day0RoasLine={day0RoasLine}
            day0RoasLineTotalFallback={day0RoasLineTotalFallback}
            isPrediction={isPrediction}
          />,
          row,
        ),
    }))

    const calendarColumns = dateRange.map((date) => ({
      code: date,
      name: (
        <div className="flex flex-col items-end">
          <div className="text-base text-black">{dayjs(date).format('MM-DD')}</div>
          <div className="text-xs text-gray-500">-</div>
        </div>
      ),
      width: 120,
      align: 'right',
      render: (value, row) => {
        if (!row.install_date) return null
        const dayIndex = dayjs(date).diff(dayjs(row.install_date), 'day')
        if (dayIndex < 0) return null
        return renderCell(
          <DayCell
            dayIndex={dayIndex}
            row={row}
            day0RoasLine={day0RoasLine}
            day0RoasLineTotalFallback={day0RoasLineTotalFallback}
            isPrediction={isPrediction}
          />,
          row,
        )
      },
    }))

    const dataColumns =
      viewMode === 'calendar' ? calendarColumns : lifecycleColumns

    return [...filteredBaseColumns, ...dataColumns]
  }, [
    roas.campaignId,
    roas?.mediaSource,
    roas?.roasLineMediaSource,
    revisedRoas,
    day0RoasLine,
    day0RoasLineTotalFallback,
    isTotal,
    handlerDayNClick,
    sorts,
    handleSort,
    dayIndices,
    isPrediction,
    columnAverages,
    sortedData,
    compareRoas,
    viewMode,
    dateRange,
  ])

  const onScrollToStart = () => {
    tableRef.current?.scrollToStart()
  }
  const onScrollToEnd = () => {
    tableRef.current?.scrollToEnd()
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mt-2">
        {!isTotal && (
          <>
            <CreateNote cid={campaignId} onCreated={handleRefrech} />
            <CampaignStatusButton campaignId={campaignId} />
          </>
        )}
        <BenchmarkView dailyData={roas?.dailyData} sourceInfo={benchmarkSourceInfo} />
        <Button variant="outline" onClick={handleExportCsv}>Export CSV</Button>
        <TipsTicker />

        <div className="flex gap-2 ml-auto items-center">
          <Tabs
            value={displayMode}
            onValueChange={setDisplayMode}
          >
            <TabsList>
              <TabsTrigger value="amount">$</TabsTrigger>
              <TabsTrigger value="percentage">%</TabsTrigger>
              <TabsTrigger value="multiplier">x</TabsTrigger>
            </TabsList>
          </Tabs>

          {displayMode === 'multiplier' && (
            <div className="flex items-center gap-2">
              <Select
                value={multiplierDenominatorMode}
                onValueChange={setMultiplierDenominatorMode}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="d0_revenue">D0 Revenue</SelectItem>
                  <SelectItem value="target_roas" disabled={!hasTargetRoas}>
                    Target ROAS {!hasTargetRoas && '(No Target ROAS Set)'}
                  </SelectItem>
                  <SelectItem value="growth_rate">Growth Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {!isPrediction && (
            <Tabs 
              value={dayMode} 
              onValueChange={setDayMode}
              disabled={viewMode === 'calendar' || displayMode === 'multiplier'}
            >
              <TabsList>
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="cumulative">Cumulative</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 bg-muted text-muted-foreground border-none hover:bg-muted/80"
              onClick={onScrollToStart}
              title="Scroll to first column"
            >
              <ChevronFirst className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 bg-muted text-muted-foreground border-none hover:bg-muted/80"
              onClick={onScrollToEnd}
              title="Scroll to last column"
            >
              <ChevronLast className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border mt-2" style={{ height: '600px' }}>
        <ROASDailyDataTable
          ref={tableRef}
          dataSource={sortedData}
          columns={columns}
          features={{
            sort: {
              sorts,
              onChange: handleSort,
            },
          }}
          primaryKey="rowId"
        />
      </div>

      <ChartCodeViewer
        open={isChartModalOpen}
        onOpenChange={setIsChartModalOpen}
        hideButton={true}
      >
        <Day0Chart
          roas={roas}
          compareRoas={compareRoas}
          dayIndex={chartDayIndex}
          day0RoasLine={day0RoasLine}
          day0RoasLineTotalFallback={day0RoasLineTotalFallback}
        />
      </ChartCodeViewer>
    </>
  )
}

export default RoasTable
